import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useData } from '../contexts/DataContext';
import { Filter, ChevronDown } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function CategoryPage() {
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState('recommended');
  const [showFilters, setShowFilters] = useState(false);
  const productsRef = useRef(null);
  const { products, categories } = useData();

  const category = categories?.find((c) => c.slug === slug);
  const filteredProducts = products?.filter((p) => p.category === slug) || [];

  useEffect(() => {
    const productCards = productsRef.current?.querySelectorAll('[data-testid^="product-card-"]');
    if (productCards && productCards.length > 0) {
      gsap.fromTo(
        productCards,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.05,
          ease: 'power3.out',
        }
      );
    }
  }, [slug, sortBy]);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="category-page">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl mb-2">
            {category?.name || 'Tüm Ürünler'}
          </h1>
          <p className="text-gray-600">{sortedProducts.length} ürün listeleniyor</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-lg hover:border-trendyol-orange transition-colors md:hidden"
            data-testid="filter-toggle-button"
          >
            <Filter className="w-5 h-5" />
            <span className="font-medium">Filtrele</span>
          </button>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <span className="text-sm text-gray-600 font-medium">Sırala:</span>
            <div className="relative flex-1 md:flex-none">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full md:w-64 appearance-none px-4 py-3 pr-10 border-2 border-gray-300 rounded-lg focus:border-trendyol-orange focus:outline-none cursor-pointer"
                data-testid="sort-select"
              >
                <option value="recommended">Önerilen</option>
                <option value="price-asc">En Düşük Fiyat</option>
                <option value="price-desc">En Yüksek Fiyat</option>
                <option value="rating">En Yüksek Puan</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          <aside
            className={`${showFilters ? 'block' : 'hidden'
              } md:block w-full md:w-64 flex-shrink-0`}
            data-testid="filters-sidebar"
          >
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-manrope font-bold text-lg mb-4">Filtreler</h3>

              <div className="mb-6">
                <h4 className="font-semibold mb-3">Fiyat Aralığı</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                    <span className="text-sm">0 - 500 TL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                    <span className="text-sm">500 - 1000 TL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                    <span className="text-sm">1000 - 5000 TL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                    <span className="text-sm">5000 TL ve üzeri</span>
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold mb-3">Kargo</h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                  <span className="text-sm">Ücretsiz Kargo</span>
                </label>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Değerlendirme Puanı</h4>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <label key={rating} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-trendyol-orange" />
                      <span className="text-sm">{rating} yıldız ve üzeri</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div ref={productsRef} className="flex-1">
            {sortedProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {sortedProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">Bu kategoride ürün bulunamadı.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
